'use client'

import * as React from 'react'
import { m, LazyMotion, domMax } from 'framer-motion'
import { ChevronLeftIcon, ChevronRightIcon } from 'primitives/icons'
import { useIsMounted } from 'hooks/use-is-mounted'

export interface ImageComparisonSliderProps {
  leftImage: string
  leftLabel?: string
  rightImage: string
  rightLabel?: string
}

export function ImageComparisonSlider(props: ImageComparisonSliderProps) {
  const { leftImage, rightImage, leftLabel, rightLabel } = props

  const [sliderValue, setSliderValue] = React.useState(50)

  const constraintsRef = React.useRef<HTMLDivElement>(null)
  const barRef = React.useRef<HTMLDivElement>(null)
  const isMounted = useIsMounted()

  function getCurrentPercentFromViewport() {
    const rect = constraintsRef.current?.getBoundingClientRect()
    const barPosition = barRef?.current?.getBoundingClientRect()

    const newPosition = barPosition?.left ? barPosition.left + 2 : 0

    const left = rect?.left ?? 0
    const length = rect?.width ?? 1

    const nextValue = Math.max(0, Math.min(1, (newPosition - left) / length))

    if (parseFloat(nextValue.toFixed(2)) === 0) {
      return 0
    }

    if (parseFloat(nextValue.toFixed(3)) > 0.998) {
      return 100
    }

    return nextValue * 100
  }

  function onUpdateProxy(nextPercent: number) {
    const clampedPercent = Math.min(Math.max(nextPercent, 0), 100)
    setSliderValue(clampedPercent)
  }

  // oxlint-disable-next-line no-nested-ternary
  const x = constraintsRef.current?.offsetWidth
    ? sliderValue > 99.998
      ? constraintsRef.current.offsetWidth
      : (sliderValue / 100) * constraintsRef.current.offsetWidth
    : 0

  const width = `${sliderValue}%`

  const animate: Record<string, string> = {}
  const animateYX: Record<string, number> = {}

  animate.width = width
  animateYX.x = x

  return (
    <div ref={constraintsRef} className="relative h-[170px] w-full sm:h-[265px] md:h-[530px]">
      <div
        style={{
          backgroundImage: `url('${leftImage}')`,
          clipPath: `inset(0 ${100 - sliderValue}% 0 0)`,
        }}
        className="rounded-4 absolute flex h-full w-full items-end bg-cover bg-center bg-no-repeat p-5"
      >
        {leftLabel ? (
          <div className="text-12 text-service-white md:text-14 flex h-[17px] w-fit min-w-[60px] items-center justify-center rounded-[50px] bg-[rgb(42,_31,_63)] px-[5px] py-3 opacity-60 md:h-[34px] md:min-w-[85px] md:py-5">
            {leftLabel}
          </div>
        ) : null}
      </div>
      <div
        style={{
          backgroundImage: `url('${rightImage}')`,
          clipPath: `inset(0 0 0 ${sliderValue}%)`,
        }}
        className="rounded-4 absolute flex h-full w-full items-end justify-end bg-cover bg-center bg-no-repeat p-5"
      >
        {rightLabel ? (
          <div className="text-12 text-service-white md:text-14 flex h-[17px] w-fit min-w-[60px] items-center justify-center rounded-[50px] bg-[rgb(42,_31,_63)] px-[5px] py-3 opacity-60 md:h-[34px] md:min-w-[85px] md:py-5">
            {rightLabel}
          </div>
        ) : null}
      </div>

      {isMounted ? (
        <LazyMotion features={domMax}>
          <m.div
            drag="x"
            dragElastic={false}
            dragConstraints={constraintsRef}
            transition={{
              duration: 0,
            }}
            dragMomentum={false}
            initial={animateYX}
            animate={animateYX}
            onDrag={() => {
              onUpdateProxy(getCurrentPercentFromViewport())
            }}
            className="relative flex h-full w-[4px] cursor-pointer flex-col"
          >
            <div
              ref={barRef}
              className="border-service-white absolute top-0 h-[calc(50%-30px)] border-2"
            />
            <div className="border-service-white absolute left-[-26px] top-[calc(50%-30px)] h-[60px] w-[60px] rounded-[50%] border-4">
              <div className="text-service-white flex h-full w-full items-center justify-center rounded-[50%] bg-[rgb(42,_31,_63)] opacity-60">
                <ChevronLeftIcon className="h-[24px] w-auto" />
                <ChevronRightIcon className="h-[24px] w-auto" />
              </div>
            </div>
            <div className="border-service-white absolute bottom-0 h-[calc(50%-30px)] border-2" />
          </m.div>
        </LazyMotion>
      ) : null}
    </div>
  )
}
